import {
    ApplePayConfig,
    applePayConfigSchema,
    AdyenCardConfig,
    adyenCardConfigSchema,
    PaypalConfig,
    paypalConfigSchema,
    PrepareConfig,
    prepareConfigSchema,
    SamsungPayConfig,
    samsungPayConfigSchema,
    GooglePayConfig,
    googlePayConfigSchema,
    AirwallexCardConfig,
    airwallexCardConfigSchema,
} from './schemas.js';
import {
    AdyenCheckout,
    CardLoadData,
    CoreConfiguration,
    CustomCard,
    CustomCardConfiguration,
    CustomTranslations,
    SubmitActions,
    SubmitData,
    UIElement,
} from '@adyen/adyen-web';
import {
    loadScript,
    OnApproveActions,
    OnApproveData,
    OnCancelledActions,
    PayPalButtonCreateOrder,
    PayPalButtonsComponentOptions,
} from '@paypal/paypal-js';
import { Env, init, Payment, createElement } from '@airwallex/components-sdk';
import { jwtDecode } from 'jwt-decode';
import { PaymentPayload, AIRWALLEX_STATUS } from './types.js';
import ApplePayMerchantCapability = ApplePayJS.ApplePayMerchantCapability;
import ApplePayValidateMerchantEvent = ApplePayJS.ApplePayValidateMerchantEvent;
import ApplePayPaymentAuthorizedEvent = ApplePayJS.ApplePayPaymentAuthorizedEvent;
import ButtonColor = google.payments.api.ButtonColor;
import ButtonType = google.payments.api.ButtonType;
import ButtonBorderType = google.payments.api.ButtonBorderType;
import ButtonSizeMode = google.payments.api.ButtonSizeMode;
import ApplePayLineItem = ApplePayJS.ApplePayLineItem;

declare global {
    interface Window {
        UpoSdk: typeof UpoSdk;
    }
}

/**
 * @class UpoSdk
 * @description UPO Payment SDK for processing payments with multiple payment providers.
 */
class UpoSdk {
    static readonly PSP = {
        adyenCard: 'ADYEN_CARD',
        paypal: 'PAYPAL',
        adyenApplePay: 'ADYEN_APPLEPAY',
        adyenSamsungPay: 'ADYEN_SAMSUNGPAY',
        adyenGooglePay: 'ADYEN_GOOGLEPAY',
        airwallexCard: 'AIRWALLEX_CARD',
    } as const;

    static readonly EVENTS = {
        PSP_ADYEN_LOADED: 'PSP_ADYEN_LOADED',
        PSP_PAYPAL_PREPARED: 'PSP_PAYPAL_PREPARED',
        PSP_PAYPAL_APPROVE_CALLBACK: 'PSP_PAYPAL_APPROVE_CALLBACK',
        PSP_INITIALIZATION_FAILED: 'PSP_INITIALIZATION_FAILED',
        PSP_PAYPAL_ON_ERROR: 'PSP_PAYPAL_ON_ERROR',
        PSP_PAYPAL_ON_CANCEL: 'PSP_PAYPAL_ON_CANCEL',
        PSP_APPLEPAY_CANCELLED: 'PSP_APPLEPAY_CANCELLED',
        PSP_APPLEPAY_PAYMENT_METHOD_SELECTED:
            'PSP_APPLEPAY_PAYMENT_METHOD_SELECTED',
        PSP_APPLEPAY_SESSION_INITIALIZATION_FAILED:
            'PSP_APPLEPAY_SESSION_INITIALIZATION_FAILED',
        PSP_APPLEPAY_COMPLETED: 'PSP_APPLEPAY_COMPLETED',
        PSP_APPLEPAY_FAILED: 'PSP_APPLEPAY_FAILED',
        PSP_SAMSUNGPAY_CANCELLED: 'PSP_SAMSUNGPAY_CANCELLED',
        PSP_SAMSUNGPAY_COMPLETED: 'PSP_SAMSUNGPAY_COMPLETED',
        PSP_SAMSUNGPAY_INITIALIZATION_FAILED:
            'PSP_SAMSUNGPAY_INITIALIZATION_FAILED',
        PSP_SAMSUNGPAY_INITIALIZATION_COMPLETED:
            'PSP_SAMSUNGPAY_INITIALIZATION_COMPLETED',
        PSP_SAMSUNGPAY_IS_READY_TO_PAY: 'PSP_SAMSUNGPAY_IS_READY_TO_PAY',
        PSP_SAMSUNGPAY_IS_READY_TO_PAY_FAILED:
            'PSP_SAMSUNGPAY_IS_READY_TO_PAY_FAILED',
        PSP_SAMSUNGPAY_LOAD_PAYMENT_SHEET_FAILED:
            'PSP_SAMSUNGPAY_LOAD_PAYMENT_SHEET_FAILED',
        PSP_SAMSUNGPAY_LOAD_PAYMENT_SHEET_LOADED:
            'PSP_SAMSUNGPAY_LOAD_PAYMENT_SHEET_LOADED',
        PSP_GOOGLEPAY_CANCELLED: 'PSP_GOOGLEPAY_CANCELLED',
        PSP_GOOGLEPAY_BUYER_ACCOUNT_ERROR: 'PSP_GOOGLEPAY_BUYER_ACCOUNT_ERROR',
        PSP_GOOGLEPAY_DEVELOPER_ERROR: 'PSP_GOOGLEPAY_DEVELOPER_ERROR',
        PSP_GOOGLEPAY_MERCHANT_ACCOUNT_ERROR:
            'PSP_GOOGLEPAY_MERCHANT_ACCOUNT_ERROR',
        PSP_GOOGLEPAY_COMPLETED: 'PSP_GOOGLEPAY_COMPLETED',
        PSP_GOOGLEPAY_INITIALIZATION_FAILED:
            'PSP_GOOGLEPAY_INITIALIZATION_FAILED',
        PSP_GOOGLEPAY_FAILED: 'PSP_GOOGLEPAY_FAILED',
        PSP_GOOGLEPAY_LOAD_PAYMENT_SHEET_FAILED:
            'PSP_GOOGLEPAY_LOAD_PAYMENT_SHEET_FAILED',
        PSP_GOOGLEPAY_LOAD_PAYMENT_SHEET_LOADED:
            'PSP_GOOGLEPAY_LOAD_PAYMENT_SHEET_LOADED',
        PSP_GOOGLEPAY_IS_READY_TO_PAY: 'PSP_GOOGLEPAY_IS_READY_TO_PAY',
        PSP_GOOGLEPAY_IS_READY_TO_PAY_FAILED:
            'PSP_GOOGLEPAY_IS_READY_TO_PAY_FAILED',
        UPO_API_PAYMENT_CHARGE_RETURNED: 'UPO_API_PAYMENT_CHARGE_RETURNED',
        UPO_ADYEN_CHARGE_RESULT: 'UPO_ADYEN_CHARGE_RESULT',
        PSP_AIRWALLEX_LOADED: 'PSP_AIRWALLEX_LOADED',
        PSP_AIRWALLEX_CHANGE: 'PSP_AIRWALLEX_CHANGE',
        PSP_AIRWALLEX_ERROR: 'PSP_AIRWALLEX_ERROR',
        PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_COMPLETED:
            'PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_COMPLETED',
        PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_FAILED:
            'PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_FAILED',
        PSP_AIRWALLEX_INITIALIZATION_FAILED:
            'PSP_AIRWALLEX_INITIALIZATION_FAILED',
        PSP_AIRWALLEX_ELEMENT_READY: 'PSP_AIRWALLEX_ELEMENT_READY',
        PSP_AIRWALLEX_ELEMENT_FOCUS: 'PSP_AIRWALLEX_ELEMENT_FOCUS',
        PSP_AIRWALLEX_ELEMENT_BLUR: 'PSP_AIRWALLEX_ELEMENT_BLUR',
        PSP_AIRWALLEX_CARD_BRAND_DETECTED: 'PSP_AIRWALLEX_CARD_BRAND_DETECTED',
        PSP_AIRWALLEX_VALIDATION_ERROR: 'PSP_AIRWALLEX_VALIDATION_ERROR',
        PSP_AIRWALLEX_ALL_FIELDS_VALID: 'PSP_AIRWALLEX_ALL_FIELDS_VALID',
        UPO_AIRWALLEX_CHARGE_RESULT: 'UPO_AIRWALLEX_CHARGE_RESULT',
        PSP_AIRWALLEX_CONFIRM_RESULT_FAILED:
            'PSP_AIRWALLEX_CONFIRM_RESULT_FAILED',
        PSP_AIRWALLEX_CONFIRM_PAYMENT_REQUIRES_ACTION:
            'PSP_AIRWALLEX_CONFIRM_PAYMENT_REQUIRES_ACTION',
        PSP_AIRWALLEX_CONFIRM_INTENT_FAILED:
            'PSP_AIRWALLEX_CONFIRM_INTENT_FAILED',
        PSP_AIRWALLEX_CONFIRM_INTENT_TIMEOUT:
            'PSP_AIRWALLEX_CONFIRM_INTENT_TIMEOUT',
    } as const;

    private apiUrl: string = 'https://api.upo.payments-test.panasonic.aero';
    private paymentJwt: string | undefined;
    private nextCloudToken: string | undefined;
    private idempotencyKey: string | undefined;
    private adyenCardConfig: AdyenCardConfig | undefined;
    private paypalConfig: PaypalConfig | undefined;
    private adyenCardComponent: CustomCard | undefined;
    private adyenApplePayConfig: ApplePayConfig | undefined;
    private adyenSamsungPayConfig: SamsungPayConfig | undefined;
    private adyenGooglePayConfig: GooglePayConfig | undefined;
    private airwallexCardConfig: AirwallexCardConfig | undefined;
    private airwallexIntentId: string | undefined;
    private airwallexClientSecret: string | undefined;
    private airwallexCardNumberElement:
        | Payment.CardNumberElementType
        | undefined;
    private airwallexExpiryElement: Payment.ExpiryDateElementType | undefined;
    private airwallexCvcElement: Payment.CvcElementType | undefined;
    private airwallexCardBrand: string | null = null;

    /**
     * Environment mapping for Airwallex.
     * Maps UPO environment values to Airwallex SDK environment values.
     */
    private readonly airwallexEnvMapping: Record<string, Env> = {
        test: 'demo',
        prod: 'prod',
        live: 'prod',
        production: 'prod',
    };

    setApiUrl(apiUrl: string): void {
        this.apiUrl = apiUrl;
    }

    // we do not need to check this is set because it has a default value
    getApiUrl(): string {
        return this.apiUrl;
    }

    setPaymentJwt(paymentJwt: string): void {
        this.paymentJwt = paymentJwt;
    }

    getPaymentJwt(): string {
        if (this.paymentJwt === undefined) {
            throw new Error(
                'paymentJwt is trying to be accessed when it has not been set',
            );
        }
        return this.paymentJwt;
    }

    getNextCloudToken(): string {
        if (this.nextCloudToken === undefined) {
            throw new Error(
                'nextCloudToken is trying to be accessed when it has not been set',
            );
        }
        return this.nextCloudToken;
    }

    setNextCloudToken(nextCloudToken: string): void {
        this.nextCloudToken = nextCloudToken;
    }

    requireIdempotencyKey(): string {
        if (this.idempotencyKey === undefined) {
            throw new Error(
                'idempotencyKey is trying to be accessed when it has not been set',
            );
        }
        return this.idempotencyKey;
    }

    requirePaymentConfigString(
        psp: (typeof UpoSdk.PSP)[keyof typeof UpoSdk.PSP],
        key: string,
    ): string {
        const value = this.getPaymentConfigValue(psp, key);
        if (typeof value === 'string') {
            return value;
        }

        throw new Error(`Config key of ${key} is not a string`);
    }

    // We type hint on any Function here because it can be a callable for multiple PSPs
    requirePaymentConfigCallback(
        psp: (typeof UpoSdk.PSP)[keyof typeof UpoSdk.PSP],
        key: string,
        // eslint-disable-next-line
    ): Function {
        const value = this.getPaymentConfigValue(psp, key);
        if (typeof value === 'function') {
            return value;
        }

        throw new Error(`Config key of ${key} is not a callback`);
    }

    private getAdyenConfigFunctionOrNull(
        key: keyof AdyenCardConfig,
    ): AdyenCardConfig[typeof key] | undefined {
        const value = this.adyenCardConfig?.[key];
        if (typeof value === 'function') {
            return value;
        }
        return undefined;
    }

    private getAdyenConfigValueOrDefault<T>(
        key: keyof AdyenCardConfig, // Restricts the `key` to be a valid key of `AdyenCardConfig`.
        type: 'string' | 'number' | 'boolean' | 'object' | 'function',
        defaultValue: T,
    ): T {
        if (
            typeof this.adyenCardConfig !== 'object' ||
            this.adyenCardConfig === null
        ) {
            return defaultValue;
        }

        const value = this.adyenCardConfig[key];

        if (typeof value === type) {
            return value as T;
        }

        return defaultValue;
    }

    private getPaymentConfigValue(
        psp: (typeof UpoSdk.PSP)[keyof typeof UpoSdk.PSP],
        key: string,
    ): any {
        let config = {};

        switch (psp) {
            case UpoSdk.PSP.airwallexCard:
                if (this.airwallexCardConfig === undefined) {
                    throw new Error(`${UpoSdk.PSP.airwallexCard} is not set`);
                }
                config = this.airwallexCardConfig;
                break;

            case UpoSdk.PSP.adyenCard:
                if (this.adyenCardConfig === undefined) {
                    throw new Error(`${UpoSdk.PSP.adyenCard} is not set`);
                }
                config = this.adyenCardConfig;
                break;

            case UpoSdk.PSP.paypal:
                if (this.paypalConfig === undefined) {
                    throw new Error(`${UpoSdk.PSP.paypal} is not set`);
                }
                config = this.paypalConfig;
                break;

            case UpoSdk.PSP.adyenApplePay:
                if (this.adyenApplePayConfig === undefined) {
                    throw new Error(`${UpoSdk.PSP.adyenApplePay} is not set`);
                }
                config = this.adyenApplePayConfig;
                break;

            case UpoSdk.PSP.adyenSamsungPay:
                if (this.adyenSamsungPayConfig === undefined) {
                    throw new Error(`${UpoSdk.PSP.adyenSamsungPay} is not set`);
                }
                config = this.adyenSamsungPayConfig;
                break;

            case UpoSdk.PSP.adyenGooglePay:
                if (this.adyenGooglePayConfig === undefined) {
                    throw new Error(`${UpoSdk.PSP.adyenGooglePay} is not set`);
                }
                config = this.adyenGooglePayConfig;
                break;

            default:
                throw new Error(`Unknown PSP type: ${psp}`);
        }

        if (!Object.prototype.hasOwnProperty.call(config, key)) {
            throw new Error(`${psp} key ${key} is not a valid key`);
        }

        const value = (config as Record<string, string | undefined>)[key];
        if (value === undefined) {
            throw new Error(`${psp} key ${key} is not set`);
        }
        return value;
    }

    async prepare(config: PrepareConfig): Promise<boolean> {
        this.mergeIntoConfig(config);

        if (this.adyenCardConfig !== undefined) {
            this.initAdyenSdk();
        }

        if (this.paypalConfig !== undefined) {
            this.initPaypalSdk();
        }

        if (this.adyenApplePayConfig !== undefined) {
            this.initApplePaySdk(this.adyenApplePayConfig);
        }

        if (this.adyenSamsungPayConfig !== undefined) {
            this.initSamsungPaySdk(this.adyenSamsungPayConfig);
        }

        if (this.adyenGooglePayConfig !== undefined) {
            this.initGooglePaySdk(this.adyenGooglePayConfig);
        }

        if (this.airwallexCardConfig !== undefined) {
            this.initAirwallexSdk(this.airwallexCardConfig);
        }
        return true;
    }

    mergeIntoConfig(config: PrepareConfig) {
        const validationResults = prepareConfigSchema.safeParse(config);
        if (!validationResults.success) {
            throw new Error(
                'Config has not passed validation ' +
                    JSON.stringify(validationResults.error),
            );
        }

        if (config.apiUrl !== undefined) {
            this.setApiUrl(config.apiUrl);
        }
        if (config.paymentJwt !== undefined) {
            this.setPaymentJwt(config.paymentJwt);
        }
        if (config.nextCloudToken !== undefined) {
            this.nextCloudToken = config.nextCloudToken;
        }
        if (config.idempotencyKey !== undefined) {
            this.idempotencyKey = config.idempotencyKey;
        }

        if (config[UpoSdk.PSP.airwallexCard] !== undefined) {
            const validationResults = airwallexCardConfigSchema.safeParse(
                config[UpoSdk.PSP.airwallexCard],
            );
            this.airwallexCardConfig = this.mergePspConfig(
                this.airwallexCardConfig,
                validationResults,
            );
        }
        if (config[UpoSdk.PSP.adyenCard] !== undefined) {
            const validationResults = adyenCardConfigSchema.safeParse(
                config[UpoSdk.PSP.adyenCard],
            );
            this.adyenCardConfig = this.mergePspConfig(
                this.adyenCardConfig,
                validationResults,
            );
        }

        if (config[UpoSdk.PSP.paypal] !== undefined) {
            const validationResults = paypalConfigSchema.safeParse(
                config[UpoSdk.PSP.paypal],
            );

            this.paypalConfig = this.mergePspConfig(
                this.paypalConfig,
                validationResults,
            );
        }

        if (config[UpoSdk.PSP.adyenApplePay] !== undefined) {
            const validationResults = applePayConfigSchema.safeParse(
                config[UpoSdk.PSP.adyenApplePay],
            );
            this.adyenApplePayConfig = this.mergePspConfig(
                this.adyenApplePayConfig,
                validationResults,
            );
        }

        if (config[UpoSdk.PSP.adyenSamsungPay] !== undefined) {
            const validationResults = samsungPayConfigSchema.safeParse(
                config[UpoSdk.PSP.adyenSamsungPay],
            );
            this.adyenSamsungPayConfig = this.mergePspConfig(
                this.adyenSamsungPayConfig,
                validationResults,
            );
        }

        if (config[UpoSdk.PSP.adyenGooglePay] !== undefined) {
            const validationResults = googlePayConfigSchema.safeParse(
                config[UpoSdk.PSP.adyenGooglePay],
            );
            this.adyenGooglePayConfig = this.mergePspConfig(
                this.adyenGooglePayConfig,
                validationResults,
            );
        }
    }

    private mergePspConfig(config: any, validationResults: any): any {
        if (!validationResults.success) {
            throw new Error(
                'Config has not passed validation ' +
                    JSON.stringify(validationResults.error),
            );
        }
        if (config === undefined) {
            return validationResults.data;
        } else {
            // Merge existing config with the new one, replacing values that are in the passed config but keep
            // values that are set and not in the passed config
            return {
                ...config,
                ...Object.fromEntries(
                    Object.entries(validationResults.data).filter(
                        ([, value]) => value !== undefined,
                    ),
                ),
            };
        }
    }

    /**
     * Charge a payment synchronously for the specified payment service provider.
     *
     * This method initiates a synchronous payment flow. Only Adyen Card payments are supported
     * for synchronous charging. PayPal and Apple Pay must use the `asyncCharge` method instead.
     *
     * @param {string} psp - The payment service provider to charge. Must be one of the PSP constants
     * specified in `UpoSdk.PSP`. Currently only `UpoSdk.PSP.adyenCard` (`'ADYEN_CARD'`) is supported
     * for synchronous charging.
     * @returns {Promise<void>} Returns a promise that resolves when the payment submission is initiated.
     * @throws {Error} If `psp` is `UpoSdk.PSP.paypal` - PayPal cannot be charged synchronously.
     * @throws {Error} If `psp` is `UpoSdk.PSP.adyenApplePay` - Apple Pay cannot be charged synchronously.
     * @throws {Error} If the Adyen Card component has not been initialized via `prepare()`.
     *
     * @example
     * ```typescript
     * const upoSdk = new UpoSdk();
     * await upoSdk.prepare(config);
     * await upoSdk.charge(UpoSdk.PSP.adyenCard);
     * ```
     */
    charge(psp: string): Promise<void> {
        console.log('Charging card...');
        if (psp === UpoSdk.PSP.paypal) {
            throw new Error(
                `${UpoSdk.PSP.paypal} cannot currently be charged synchrously`,
            );
        }

        if (psp === UpoSdk.PSP.adyenApplePay) {
            throw new Error(
                `${UpoSdk.PSP.adyenApplePay} cannot currently be charged synchronously`,
            );
        }

        if (
            psp === UpoSdk.PSP.adyenCard &&
            this.adyenCardComponent !== undefined
        ) {
            this.adyenCardComponent?.submit();
        }

        if (psp === UpoSdk.PSP.airwallexCard) {
            this.chargeAirwallex();
        }

        return Promise.resolve();
    }

    /**
     * Charge a payment asynchronously for the specified payment service provider.
     *
     * This method initiates an asynchronous payment flow by sending a charge request to the UPO API.
     * Supported payment service providers for asynchronous charging include PayPal, Apple Pay,
     * Samsung Pay, and Google Pay. Adyen Card payments must use the `charge` method instead.
     *
     * Emits the `UPO_API_PAYMENT_CHARGE_RETURNED` event when the API responds.
     *
     * @param {string} psp - The payment service provider to charge. Must be one of the
     * PSP constants specified in `UpoSdk.PSP`. Supported values for asynchronous charging:
     * - `UpoSdk.PSP.paypal` (`'PAYPAL'`)
     * - `UpoSdk.PSP.adyenApplePay` (`'ADYEN_APPLEPAY'`)
     * - `UpoSdk.PSP.adyenSamsungPay` (`'ADYEN_SAMSUNGPAY'`)
     * - `UpoSdk.PSP.adyenGooglePay` (`'ADYEN_GOOGLEPAY'`)
     * @param {Object} [additionalData={}] - Additional payment data required by certain PSPs.
     * For Apple Pay: must contain `payment.token.paymentData` (the encrypted Apple Pay token).
     * For Samsung Pay: must contain `['3DS'].data` (the 3DS payment data).
     * For Google Pay: must contain `paymentMethodData.tokenizationData.token` (the Google Pay token).
     * @returns {Promise<void>} Returns a promise that resolves when the payment charge is complete.
     * @throws {Error} If `psp` is `UpoSdk.PSP.adyenCard` - Adyen Card cannot be charged asynchronously.
     * @throws {Error} If the SDK has not been properly configured via `prepare()`.
     * @throws {Error} If the payment charge fails (non-200 response from the API).
     *
     * @example
     * ```typescript
     * const upoSdk = new UpoSdk();
     * await upoSdk.prepare(config);
     *
     * // For PayPal (typically called automatically by PayPal)
     * await upoSdk.asyncCharge(UpoSdk.PSP.paypal);
     *
     * // For Apple Pay with payment data
     * await upoSdk.asyncCharge(UpoSdk.PSP.adyenApplePay, {
     *     payment: {
     *         token: {
     *             paymentData: 'encryptedToken...'
     *         }
     *     }
     * });
     *
     * // For Samsung Pay with 3DS data
     * await upoSdk.asyncCharge(UpoSdk.PSP.adyenSamsungPay, {
     *     '3DS': { data: '3DSToken...' }
     * });
     *
     * // For Google Pay with payment method data
     * await upoSdk.asyncCharge(UpoSdk.PSP.adyenGooglePay, {
     *     paymentMethodData: {
     *         tokenizationData: {
     *             token: 'googlePayToken...'
     *         }
     *     }
     * });
     * ```
     */
    async asyncCharge(
        psp: string,
        additionalData: Record<string, any> = {},
    ): Promise<void> {
        if (psp === UpoSdk.PSP.adyenCard) {
            throw new Error(
                `${UpoSdk.PSP.adyenCard} cannot currently be charged asynchronously`,
            );
        }

        if (psp === UpoSdk.PSP.paypal) {
            const storeId = this.requirePaymentConfigString(
                UpoSdk.PSP.paypal,
                'storeId',
            );
            const token = jwtDecode<PaymentPayload>(this.getPaymentJwt());
            const url = `${this.getApiUrl()}/payments/${storeId}/transaction/${this.requireIdempotencyKey()}/charge`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: this.prepareToken(this.getNextCloudToken()),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    merchantProfileId: this.requirePaymentConfigString(
                        UpoSdk.PSP.paypal,
                        'merchantProfileId',
                    ),
                    amount: token.upoAmount,
                }),
            });

            this.emitEvent(
                UpoSdk.EVENTS.UPO_API_PAYMENT_CHARGE_RETURNED,
                response,
            );
            return Promise.resolve();
        }

        if (psp === UpoSdk.PSP.adyenApplePay) {
            console.log('Adyen Apple Pay: asyncCharge');
            const storeId = this.requirePaymentConfigString(
                UpoSdk.PSP.adyenApplePay,
                'storeId',
            );
            console.log('Adyen Apple Pay: storeId', storeId);
            const token = jwtDecode<PaymentPayload>(this.getPaymentJwt());
            console.log('Adyen Apple Pay: token', token);
            const url = `${this.getApiUrl()}/payments/${storeId}/transaction/${this.requireIdempotencyKey()}/charge`;
            console.log('Adyen Apple Pay: url', url);
            const merchantProfileId = this.requirePaymentConfigString(
                UpoSdk.PSP.adyenApplePay,
                'merchantProfileId',
            );
            console.log('merchantProfileId', merchantProfileId);
            const payload = {
                encryptionData: additionalData.payment.token.paymentData,
                amount: token.upoAmount,
                returnUrl: '',
                merchantProfileId: merchantProfileId,
            };
            console.log('Adyen Apple Pay: payload', payload);
            const authorization = this.prepareToken(this.getNextCloudToken());

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: authorization,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            this.emitEvent(
                UpoSdk.EVENTS.UPO_API_PAYMENT_CHARGE_RETURNED,
                response,
            );

            if (response.status !== 200) {
                throw new Error('Error returned from UPO ' + response.status);
            }

            return Promise.resolve();
        }

        if (psp === UpoSdk.PSP.adyenSamsungPay) {
            console.log('Adyen Samsung Pay: asyncCharge');
            const storeId = this.requirePaymentConfigString(
                UpoSdk.PSP.adyenSamsungPay,
                'storeId',
            );
            console.log('Adyen Samsung Pay: storeId', storeId);
            const token = jwtDecode<PaymentPayload>(this.getPaymentJwt());
            console.log('Adyen Samsung Pay: token', token);
            const url = `${this.getApiUrl()}/payments/${storeId}/transaction/${this.requireIdempotencyKey()}/charge`;
            console.log('Adyen Samsung Pay: url', url);
            const merchantProfileId = this.requirePaymentConfigString(
                UpoSdk.PSP.adyenSamsungPay,
                'merchantProfileId',
            );
            console.log('merchantProfileId', merchantProfileId);
            const payload = {
                samsungPayData: JSON.stringify(additionalData),
                amount: token.upoAmount,
                returnUrl: '',
                merchantProfileId: merchantProfileId,
            };
            console.log('Adyen Samsung Pay: payload', payload);
            const authorization = this.prepareToken(this.getNextCloudToken());

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: authorization,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            this.emitEvent(
                UpoSdk.EVENTS.UPO_API_PAYMENT_CHARGE_RETURNED,
                response,
            );

            if (response.status !== 200) {
                throw new Error('Error returned from UPO ' + response.status);
            }

            return Promise.resolve();
        }

        if (psp === UpoSdk.PSP.adyenGooglePay) {
            console.log('Adyen Google Pay: asyncCharge');
            const storeId = this.requirePaymentConfigString(
                UpoSdk.PSP.adyenGooglePay,
                'storeId',
            );
            console.log('Adyen Google Pay: storeId', storeId);
            const token = jwtDecode<PaymentPayload>(this.getPaymentJwt());
            console.log('Adyen Google Pay: token', token);
            const url = `${this.getApiUrl()}/payments/${storeId}/transaction/${this.requireIdempotencyKey()}/charge`;
            console.log('Adyen Google Pay: url', url);
            const merchantProfileId = this.requirePaymentConfigString(
                UpoSdk.PSP.adyenGooglePay,
                'merchantProfileId',
            );
            console.log('merchantProfileId', merchantProfileId);
            const payload = {
                googlePayTokenData:
                    additionalData.paymentMethodData.tokenizationData.token,
                amount: token.upoAmount,
                returnUrl: '',
                merchantProfileId: merchantProfileId,
            };
            console.log('Adyen Google Pay payload: ', payload);
            const authorization = this.prepareToken(this.getNextCloudToken());

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: authorization,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            this.emitEvent(
                UpoSdk.EVENTS.UPO_API_PAYMENT_CHARGE_RETURNED,
                response,
            );

            if (response.status !== 200) {
                throw new Error('Error returned from UPO ' + response.status);
            }

            return Promise.resolve();
        }
    }

    /**
     * Initialize the Airwallex SDK for card payments.
     *
     * This method orchestrates the backend calls required to initialize Airwallex:
     * 1. Calls checkout-and-transcation endpoint
     * 2. Returns client-side data (intentId, clientSecret)
     *
     * @param {Object} config - Airwallex configuration (optional if alread set via prepare())
     * @param {string} [config.merchantProfileId] - UUID of the merchant profile
     * @param {string} [config.storeId] - UUID of the store
     * @param {string} [config.invoiceId] - Invoice ID for the transaction
     * @param {string} [config.paymentReference] - Payment reference for the transaction
     * @param {function} [config.onInitializeTransaction] - Optional callback for custom initialization
     * @returns {Promise<{intentId: string, clientSecret: string, checkoutSessionId?: string}>} Client-side data for Airwallex SDK
     * @throws {Error} If initialization fails or required parameters are missing
     */
    private async chargeAirwallex() {
        const confirmIntent = await this.confirmAirwallexPaymentIntent();

        if (!confirmIntent) {
            // Timeout occurred - log warning but continue the flow
            this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_CONFIRM_INTENT_TIMEOUT, {
                message:
                    'Airwallex confirm intent timed out, proceeding with flow',
            });
        }

        const confirmResultResponse =
            await this.sendAirwallexConfirmIntentResponseToUpo(
                this.airwallexIntentId!,
                this.getNextCloudToken(),
                this.requireIdempotencyKey(),
            );

        if (confirmResultResponse.errorType) {
            this.emitEvent(
                UpoSdk.EVENTS.PSP_AIRWALLEX_CONFIRM_RESULT_FAILED,
                confirmResultResponse.message,
            );
            throw new Error('Confirm Airwallex result failed.');
        }
        if (
            confirmResultResponse.status ===
            AIRWALLEX_STATUS.REQUIRES_PAYMENT_METHOD
        ) {
            this.emitEvent(
                UpoSdk.EVENTS.PSP_AIRWALLEX_CONFIRM_PAYMENT_REQUIRES_ACTION,
                {
                    errorType: AIRWALLEX_STATUS.REQUIRES_PAYMENT_METHOD,
                    message:
                        'Payment confirmation did not reach a terminal state.',
                },
            );
            throw new Error(
                'Payment confirmation did not reach a terminal state.',
            );
        }
        this.emitEvent(UpoSdk.EVENTS.UPO_AIRWALLEX_CHARGE_RESULT, {
            status: confirmResultResponse.status,
            message: confirmResultResponse.message,
        });
    }

    private async confirmAirwallexPaymentIntent(): Promise<any> {
        try {
            if (!this.airwallexClientSecret) {
                throw new Error('Airwallex client secret is not initialized');
            }
            const confirmIntentReq = {
                client_secret: this.airwallexClientSecret,
                payment_method_options: {
                    card: {
                        auto_capture: this.airwallexCardConfig?.autoCapture,
                    },
                },
            };

            // Create a timeout promise that resolves with undefined after 3 seconds
            const timeoutPromise = new Promise<undefined>((resolve) => {
                setTimeout(() => {
                    console.warn(
                        'Airwallex confirm intent timed out after 3 seconds',
                    );
                    resolve(undefined);
                }, 3000);
            });

            // Race between the confirm call and the timeout
            const confirmIntentResponse = await Promise.race([
                this.airwallexCardNumberElement?.confirm(confirmIntentReq),
                timeoutPromise,
            ]);

            return confirmIntentResponse;
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    private async sendAirwallexConfirmIntentResponseToUpo(
        intentId: string,
        authToken: string,
        idempotencyKey: string,
    ) {
        try {
            if (!intentId) {
                throw new Error('Intent id is required');
            }
            if (!authToken) {
                throw new Error('Auth token is required');
            }
            if (!idempotencyKey) {
                throw new Error('Idempotency key is required');
            }

            const url = `${this.getApiUrl()}/psp/airwallex/confirm-result`;

            const requestBody: Airwallex.ConfirmResultRequest = {
                externalTransactionId: idempotencyKey,
                paymentMethod: 'card',
                intentId: intentId,
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: this.prepareToken(authToken),
                    'Idempotency-Key': idempotencyKey,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API error : ', response.status, errorText);
                throw new Error(
                    `API called failed with status : ${response.status} : ${errorText}`,
                );
            }

            console.log('Airwallex result confirmed');
            const responseData = await response.json();
            return responseData;
        } catch (error) {
            console.error('Error:', error);
            throw error;
        }
    }

    /**
     * Get an Airwallex config value or return a default.
     * Similar to getAdyenConfigValueOrDefault() used in initAdyenSdk()
     *
     * @param key - The key from AirwallexCardConfig
     * @param type - The expected type of the value
     * @param defaultValue - The default value to return if not set
     * @returns The config value or default
     */
    private getAirwallexConfigValueOrDefault<T>(
        key: keyof AirwallexCardConfig,
        type: 'string' | 'number' | 'boolean' | 'object' | 'function',
        defaultValue: T,
    ): T {
        if (
            typeof this.airwallexCardConfig !== 'object' ||
            this.airwallexCardConfig === null
        ) {
            return defaultValue;
        }

        const value = this.airwallexCardConfig[key];

        if (typeof value === type) {
            return value as T;
        }

        return defaultValue;
    }

    /**
     * Mount the Airwallex card elements to the DOM
     *
     * This method loads the Airwallex SDK, initializes it, creates card elements,
     * and mounts them to the specified DOM elements. Must be called after
     * initiAirwallexSdk()
     *
     * @throws {Error} If Airwallex has not been initialized
     * @throws {Error} If mounting elements are not specified in config
     */
    public async initAirwallexSdk(config = {}) {
        if (typeof window === 'undefined') {
            throw new Error(
                'initAirwallex cannot be called in a non-browser environment',
            );
        }

        if (config !== undefined && Object.keys(config).length > 0) {
            const validationResults =
                airwallexCardConfigSchema.safeParse(config);
            if (!validationResults.success) {
                throw new Error(
                    'Airwallex config validation failed: ' +
                        JSON.stringify(validationResults.error),
                );
            }

            if (this.airwallexCardConfig === undefined) {
                this.airwallexCardConfig = validationResults.data;
            } else {
                this.airwallexCardConfig = {
                    ...this.airwallexCardConfig,
                    ...Object.fromEntries(
                        Object.entries(validationResults.data).filter(
                            ([, value]) => value !== undefined,
                        ),
                    ),
                };
            }
        }

        if (!this.airwallexCardConfig) {
            throw new Error('Airwallex card config not set');
        }

        const configuration: Airwallex.AirwallexConfiguration = {
            environment: this.getAirwallexConfigValueOrDefault<string>(
                'environment',
                'string',
                'test',
            ),
            cardNumberElement: this.getAirwallexConfigValueOrDefault<string>(
                'cardNumberElement',
                'string',
                'airwallex-card-number',
            ),
            expiryElement: this.getAirwallexConfigValueOrDefault<string>(
                'expiryElement',
                'string',
                'airwallex-expiry',
            ),
            cvcElement: this.getAirwallexConfigValueOrDefault<string>(
                'cvcElement',
                'string',
                'airwallex-cvc',
            ),
        };

        const env =
            this.airwallexEnvMapping[configuration.environment] ?? 'demo';

        try {
            await init({
                env: env,
                enabledElements: ['payments'],
            });

            this.airwallexCardNumberElement = await createElement(
                'cardNumber',
                { placeholder: '' },
            );
            if (!this.airwallexCardNumberElement) {
                throw new Error(
                    'Failed to create Airwallex card number element',
                );
            }

            this.airwallexExpiryElement = await createElement('expiry');
            if (!this.airwallexExpiryElement) {
                throw new Error(
                    'Failed to create Airwallex card expiry element',
                );
            }

            this.airwallexCvcElement = await createElement('cvc');
            if (!this.airwallexCvcElement) {
                throw new Error('Failed to create Airwallex card CVC element');
            }

            this.airwallexCardNumberElement.mount(
                configuration.cardNumberElement,
            );
            this.airwallexExpiryElement.mount(configuration.expiryElement);
            this.airwallexCvcElement.mount(configuration.cvcElement);

            this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_LOADED, {});
        } catch (error) {
            console.error('Failed to mount Airwallex elements: ', error);
            this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_INITIALIZATION_FAILED, {
                error: error instanceof Error ? error.message : String(error),
            });
        }
        this.setupAirwallexEventHandlers();
        this.setupAirwallexTransactionInitializerListener();
    }

    private async setupAirwallexTransactionInitializerListener() {
        const buttonSelector = this.airwallexCardConfig?.chargeButtonElement;
        if (!buttonSelector) {
            return;
        }
        const airwallexChargeButton =
            document.querySelector<HTMLButtonElement>(buttonSelector);
        if (!airwallexChargeButton) return;
        airwallexChargeButton.addEventListener('click', async () => {
            airwallexChargeButton.disabled = true;
            airwallexChargeButton.textContent = 'Charging...';

            const merchantProfileId = this.requirePaymentConfigString(
                UpoSdk.PSP.airwallexCard,
                'merchantProfileId',
            );
            const storeId = this.requirePaymentConfigString(
                UpoSdk.PSP.airwallexCard,
                'storeId',
            );
            const invoiceId = this.requirePaymentConfigString(
                UpoSdk.PSP.airwallexCard,
                'invoiceId',
            );

            const token = jwtDecode<PaymentPayload>(this.getPaymentJwt());

            const reqObj = {
                amount: {
                    value: token.upoAmount.value,
                    currency: this.airwallexCardConfig?.airwallexCurrency,
                },
                invoiceId: invoiceId,
                storeId: storeId,
                merchantProfileId: merchantProfileId,
                authToken: this.prepareToken(this.getNextCloudToken()),
                idempotencyKey: this.requireIdempotencyKey(),
                apiUrl: this.getApiUrl(),
            };
            try {
                const callback = this.requirePaymentConfigCallback(
                    UpoSdk.PSP.airwallexCard,
                    'onInitializeTransaction',
                );

                const cardCredentials = await callback(reqObj);

                if (!cardCredentials) {
                    console.error('Missing card credentials in response:');
                    this.emitEvent(
                        UpoSdk.EVENTS
                            .PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_FAILED,
                        {
                            error: 'Invalid response: missing cardCredentials',
                        },
                    );
                    throw new Error(
                        'Airwallex initialization failed: invalid response from server (missing cardCredentials)',
                    );
                }

                if (!cardCredentials.intentId) {
                    console.error('Missing intentId in card credentials:');
                    this.emitEvent(
                        UpoSdk.EVENTS
                            .PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_FAILED,
                        {
                            error: 'Invalid response: missing intentId',
                        },
                    );
                    throw new Error(
                        'Airwallex initialization failed: missing intentId in response',
                    );
                }

                if (!cardCredentials.clientSecret) {
                    console.error('Missing clientSecret in card credentials:');
                    this.emitEvent(
                        UpoSdk.EVENTS
                            .PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_FAILED,
                        {
                            error: 'Invalid response: missing clientSecret',
                        },
                    );
                    throw new Error(
                        'Airwallex initialization failed: missing clientSecret in response',
                    );
                }

                this.airwallexIntentId = cardCredentials.intentId;
                this.airwallexClientSecret = cardCredentials.clientSecret;

                this.emitEvent(
                    UpoSdk.EVENTS
                        .PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_COMPLETED,
                    {},
                );
            } catch (error) {
                console.error('onInitializeTransaction error : ', error);
                this.emitEvent(
                    UpoSdk.EVENTS
                        .PSP_AIRWALLEX_TRANSACTION_INITIALIZATION_FAILED,
                    { error: error },
                );
            }
        });
    }

    /**
     * Setup event handlers for Airwallex card elements
     */
    private setupAirwallexEventHandlers(): void {
        if (!this.airwallexCardNumberElement) {
            console.warn('No card element available');
            return;
        }

        // Local state for tracking element status within this method
        const airwallexElementsReady = {
            cardNumber: false,
            expiry: false,
            cvc: false,
        };
        const airwallexElementsComplete = {
            cardNumber: false,
            expiry: false,
            cvc: false,
        };
        const airwallexElementsValid = {
            cardNumber: false,
            expiry: false,
            cvc: false,
        };

        this.airwallexCardNumberElement.on('ready', (event: any) => {
            airwallexElementsReady.cardNumber = true;
            this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_READY, {
                elementType: 'cardNumber',
                event,
            });

            if (typeof this.airwallexCardConfig?.onReady === 'function') {
                this.airwallexCardConfig.onReady(event);
            }
        });

        this.airwallexCardNumberElement.on('focus', (event: any) => {
            this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_FOCUS, {
                elementType: 'cardNumber',
                event,
            });
        });

        this.airwallexCardNumberElement.on('blur', (event: any) => {
            this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_BLUR, {
                elementType: 'cardNumber',
                event,
            });
        });

        this.airwallexCardNumberElement.on('change', (event: any) => {
            this.handleElementValidation('cardNumber', event.detail);
            this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_CHANGE, {
                elementType: 'cardNumber',
                event: event.detail,
            });

            if (event.detail?.complete !== undefined) {
                airwallexElementsComplete.cardNumber = event.detail.complete;
            }

            if (
                event.detail?.card?.brand &&
                event.detail.card.brand !== this.airwallexCardBrand
            ) {
                this.airwallexCardBrand = event.detail.card.brand;
                this.emitEvent(
                    UpoSdk.EVENTS.PSP_AIRWALLEX_CARD_BRAND_DETECTED,
                    {
                        brand: event.detail.card.brand,
                        event,
                    },
                );
            }

            if (event.detail?.error) {
                airwallexElementsValid.cardNumber = false;
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_VALIDATION_ERROR, {
                    elementType: 'cardNumber',
                    error: event.detail.error,
                    event,
                });
            } else {
                airwallexElementsValid.cardNumber = true;
                if (
                    airwallexElementsValid.cardNumber &&
                    airwallexElementsValid.expiry &&
                    airwallexElementsValid.cvc
                ) {
                    this.emitEvent(
                        UpoSdk.EVENTS.PSP_AIRWALLEX_ALL_FIELDS_VALID,
                        {},
                    );
                }
            }
        });

        if (this.airwallexExpiryElement) {
            this.airwallexExpiryElement.on('ready', (event: any) => {
                airwallexElementsReady.expiry = true;
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_READY, {
                    elementType: 'expiry',
                    event,
                });
            });

            this.airwallexExpiryElement.on('focus', (event: any) => {
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_FOCUS, {
                    elementType: 'expiry',
                    event,
                });
            });

            this.airwallexExpiryElement.on('blur', (event: any) => {
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_BLUR, {
                    elementType: 'expiry',
                    event,
                });
            });

            this.airwallexExpiryElement.on('change', (event: any) => {
                this.handleElementValidation('expiry', event.detail);
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_CHANGE, {
                    elementType: 'expiry',
                    event,
                });

                if (event.detail?.complete !== undefined) {
                    airwallexElementsComplete.expiry = event.detail.complete;
                }

                if (event.detail?.error) {
                    airwallexElementsValid.expiry = false;
                    this.emitEvent(
                        UpoSdk.EVENTS.PSP_AIRWALLEX_VALIDATION_ERROR,
                        {
                            elementType: 'expiry',
                            error: event.detail.error,
                            event,
                        },
                    );
                } else {
                    airwallexElementsValid.expiry = true;
                    if (
                        airwallexElementsValid.cardNumber &&
                        airwallexElementsValid.expiry &&
                        airwallexElementsValid.cvc
                    ) {
                        this.emitEvent(
                            UpoSdk.EVENTS.PSP_AIRWALLEX_ALL_FIELDS_VALID,
                            event,
                        );
                    }
                }
            });
        }

        if (this.airwallexCvcElement) {
            this.airwallexCvcElement.on('ready', (event: any) => {
                airwallexElementsReady.cvc = true;
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_READY, {
                    elementType: 'cvc',
                    event,
                });
            });

            this.airwallexCvcElement.on('focus', (event: any) => {
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_FOCUS, {
                    elementType: 'cvc',
                    event,
                });
            });

            this.airwallexCvcElement.on('blur', (event: any) => {
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_ELEMENT_BLUR, {
                    elementType: 'cvc',
                    event,
                });
            });

            this.airwallexCvcElement.on('change', (event: any) => {
                this.handleElementValidation('cvc', event.detail);
                this.emitEvent(UpoSdk.EVENTS.PSP_AIRWALLEX_CHANGE, {
                    elementType: 'cvc',
                    event,
                });

                if (event.detail?.complete !== undefined) {
                    airwallexElementsComplete.cvc = event.detail.complete;
                }

                if (event.detail?.error) {
                    airwallexElementsValid.cvc = false;
                    this.emitEvent(
                        UpoSdk.EVENTS.PSP_AIRWALLEX_VALIDATION_ERROR,
                        {
                            elementType: 'cvc',
                            error: event.detail.error,
                            event,
                        },
                    );
                } else {
                    airwallexElementsValid.cvc = true;
                    if (
                        airwallexElementsValid.cardNumber &&
                        airwallexElementsValid.expiry &&
                        airwallexElementsValid.cvc
                    ) {
                        this.emitEvent(
                            UpoSdk.EVENTS.PSP_AIRWALLEX_ALL_FIELDS_VALID,
                            event,
                        );
                    }
                }
            });
        }
    }

    private handleElementValidation(
        elementType: 'cardNumber' | 'expiry' | 'cvc',
        detail: {
            complete: boolean;
            empty: boolean;
            error?: { code: string; message: string };
        },
    ): void {
        const { complete, error } = detail;
        const elementId = `airwallex-${elementType === 'cardNumber' ? 'card-number' : elementType}`;
        const container = document.getElementById(elementId);

        if (!container) return;

        container.style.borderColor = '';
        container.classList.remove(
            'border-red-500',
            'border-green-500',
            'border-gray-300',
        );

        if (error) {
            container.classList.add('border-red-500');
            container.style.borderColor = '#ef444';
        } else if (complete) {
            container.classList.add('border-green-500');
            container.style.borderColor = '#22c55e';
        }
    }

    /**
     * Initialize the Adyen SDK for card payments.
     *
     * This method sets up the Adyen Checkout SDK including configuration, card component mounting,
     * and event handling. Must be called after `prepare()` when Adyen Card is configured.
     *
     * The method performs the following:
     * - Validates that Adyen Card configuration is present
     * - Creates an AdyenCheckout instance with core configuration (environment, clientKey, etc.)
     * - Configures the `onSubmit` handler which sends payment data to the UPO API
     * - Sets up optional callbacks (onEnterKeyPressed, onError, onChange) if provided in config
     * - Creates and mounts a CustomCard component to the specified DOM element
     * - Configures card component callbacks (onLoad, onBrand, onAllValid, etc.)
     * - Emits the `PSP_ADYEN_LOADED` event when the card component is loaded
     *
     * Emits the `UPO_ADYEN_CHARGE_RESULT` event when the payment charge is processed.
     *
     * @throws {Error} If Adyen Card configuration has not been set via `prepare()`.
     * @throws {Error} If the mounting element specified in configuration cannot be found.
     */
    private async initAdyenSdk() {
        if (!this.adyenCardConfig) {
            throw new Error('Adyen Card is not configured');
        }

        const configuration: CoreConfiguration = {
            environment: this.adyenCardConfig.environment,
            clientKey: this.requirePaymentConfigString(
                UpoSdk.PSP.adyenCard,
                'clientKey',
            ),
            showPayButton: this.getAdyenConfigValueOrDefault<boolean>(
                'showPayButton',
                'boolean',
                false,
            ) as boolean,
            countryCode: this.getAdyenConfigValueOrDefault<string>(
                'countryCode',
                'string',
                'US',
            ) as string,
            locale: this.getAdyenConfigValueOrDefault<string>(
                'locale',
                'string',
                'en-US',
            ) as string,
            translations: this.getAdyenConfigValueOrDefault<object>(
                'translations',
                'object',
                {},
            ) as CustomTranslations,

            onSubmit: (
                data: SubmitData,
                component: UIElement,
                actions: SubmitActions,
            ) => {
                try {
                    if (data === undefined || !data.isValid) {
                        throw new Error('Card data is invalid');
                    }

                    if (
                        this.adyenCardConfig?.cardNameElement !== undefined &&
                        this.adyenCardConfig?.cardNameElement !== null &&
                        this.adyenCardConfig?.cardNameElement !== ''
                    ) {
                        const cardHolderName = document.querySelector(
                            this.adyenCardConfig?.cardNameElement,
                        );
                        if (cardHolderName instanceof HTMLInputElement) {
                            data.data.paymentMethod.holderName =
                                cardHolderName.value;
                        }
                    }

                    let paymentMethod = 'card';
                    if (this.adyenCardConfig?.paymentMethod !== undefined) {
                        paymentMethod = this.adyenCardConfig.paymentMethod;
                    }

                    const url = `${this.getApiUrl()}/psp/adyen/jwt-sync-charge`;
                    const payload = {
                        storeId: this.requirePaymentConfigString(
                            UpoSdk.PSP.adyenCard,
                            'storeId',
                        ),
                        merchantProfileId: this.requirePaymentConfigString(
                            UpoSdk.PSP.adyenCard,
                            'merchantProfileId',
                        ),
                        paymentMethod: paymentMethod,
                        paymentRequestJwt: this.getPaymentJwt(),
                        tokenizePaymentMethod:
                            this.adyenCardConfig?.tokenizePaymentMethod,
                        idempotencyKey: this.requireIdempotencyKey(),
                        tokenId: this.adyenCardConfig?.tokenId,
                        adyenData: data,
                    };

                    fetch(url, {
                        method: 'POST',
                        headers: {
                            Authorization: this.prepareToken(
                                this.getNextCloudToken(),
                            ),
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                    })
                        .then((res) => res.json())
                        .then((data) => {
                            this.emitEvent(
                                UpoSdk.EVENTS.UPO_ADYEN_CHARGE_RESULT,
                                data,
                            );
                        });
                } catch (e) {
                    console.error(e);
                    throw e;
                }
            },
        };

        if (
            typeof this.adyenCardConfig?.onEnterKeyPressedCallback ===
            'function'
        ) {
            configuration.onEnterKeyPressed =
                this.adyenCardConfig?.onEnterKeyPressedCallback;
        }

        if (typeof this.adyenCardConfig?.onErrorCallback === 'function') {
            configuration.onError = this.adyenCardConfig?.onErrorCallback;
        }

        if (typeof this.adyenCardConfig?.onChangeCallback === 'function') {
            configuration.onChange = this.adyenCardConfig?.onChangeCallback;
        }

        if (typeof window !== 'undefined') {
            const checkout = await AdyenCheckout(configuration);
            let cardConfig = {
                onLoad: () => {
                    this.emitEvent(UpoSdk.EVENTS.PSP_ADYEN_LOADED, {});
                    if (
                        typeof this.adyenCardConfig?.onLoadCallback ===
                        'function'
                    ) {
                        const cardLoadData: CardLoadData = {
                            iframesLoaded: true,
                        };
                        this.adyenCardConfig?.onLoadCallback(cardLoadData);
                    }
                },
            } as CustomCardConfiguration;

            if (this.getAdyenConfigFunctionOrNull('cardComponentOnBrand')) {
                cardConfig.onBrand = this.adyenCardConfig?.cardComponentOnBrand;
            }
            if (this.getAdyenConfigFunctionOrNull('cardComponentOnAllValid')) {
                cardConfig.onAllValid =
                    this.adyenCardConfig?.cardComponentOnAllValid;
            }
            if (
                this.getAdyenConfigFunctionOrNull(
                    'cardComponentOnConfigSuccess',
                )
            ) {
                cardConfig.onConfigSuccess =
                    this.adyenCardConfig?.cardComponentOnConfigSuccess;
            }
            if (this.getAdyenConfigFunctionOrNull('cardComponentOnFocus')) {
                cardConfig.onFocus = this.adyenCardConfig?.cardComponentOnFocus;
            }
            if (this.getAdyenConfigFunctionOrNull('cardComponentOnBinLookup')) {
                cardConfig.onBinLookup =
                    this.adyenCardConfig?.cardComponentOnBinLookup;
            }
            if (this.getAdyenConfigFunctionOrNull('cardComponentOnBinValue')) {
                cardConfig.onBinValue =
                    this.adyenCardConfig?.cardComponentOnBinValue;
            }
            if (
                this.getAdyenConfigFunctionOrNull(
                    'cardComponentOnEnterKeyPressed',
                )
            ) {
                cardConfig.onEnterKeyPressed =
                    this.adyenCardConfig?.cardComponentOnEnterKeyPressed;
            }
            if (
                this.getAdyenConfigFunctionOrNull('cardComponentOnFieldValid')
            ) {
                cardConfig.onFieldValid =
                    this.adyenCardConfig?.cardComponentOnFieldValid;
            }
            if (
                this.getAdyenConfigFunctionOrNull(
                    'cardComponentOnValidationError',
                )
            ) {
                cardConfig.onValidationError =
                    this.adyenCardConfig?.cardComponentOnValidationError;
            }

            cardConfig.styles = this.getAdyenConfigValueOrDefault<object>(
                'cardComponentStyles',
                'object',
                {
                    error: { color: 'red' },
                    placeholder: { color: 'blue' },
                    validated: { color: 'green' },
                },
            );
            cardConfig.autoFocus = this.getAdyenConfigValueOrDefault<boolean>(
                'cardComponentAutoFocus',
                'boolean',
                true,
            );

            if (
                this.getAdyenConfigFunctionOrNull('onValidationErrorCallback')
            ) {
                cardConfig.onValidationError =
                    this.adyenCardConfig?.onValidationErrorCallback;
            }
            if (this.getAdyenConfigFunctionOrNull('onAllValidCallback')) {
                cardConfig.onAllValid =
                    this.adyenCardConfig?.onAllValidCallback;
            }
            if (this.getAdyenConfigFunctionOrNull('onFieldValidCallback')) {
                cardConfig.onFieldValid =
                    this.adyenCardConfig?.onFieldValidCallback;
            }
            if (this.getAdyenConfigFunctionOrNull('onBrandCallback')) {
                cardConfig.onBrand = this.adyenCardConfig?.onBrandCallback;
            }

            // If brands config isn't passed then default to all accepted brands
            cardConfig.brands = this.adyenCardConfig?.cardComponentBrands ?? [
                'amex',
                'argencard',
                'bcmc',
                'bijcard',
                'cabal',
                'cartebancaire',
                'codensa',
                'cup',
                'dankort',
                'diners',
                'discover',
                'electron',
                'elo',
                'forbrugsforeningen',
                'hiper',
                'hipercard',
                'jcb',
                'karenmillen',
                'laser',
                'maestro',
                'maestrouk',
                'mc',
                'mcalphabankbonus',
                'mir',
                'naranja',
                'oasis',
                'rupay',
                'shopping',
                'solo',
                'troy',
                'uatp',
                'visa',
                'visaalphabankbonus',
                'visadankort',
                'warehouse',
            ];

            if (this.adyenCardConfig?.tokenId !== undefined) {
                cardConfig.brands?.push('token');
            }

            this.adyenCardComponent = new CustomCard(
                checkout,
                cardConfig,
            ).mount(
                this.requirePaymentConfigString(
                    UpoSdk.PSP.adyenCard,
                    'mountingElement',
                ),
            );
        }
    }

    /**
     * Initialize the PayPal SDK for payment processing.
     *
     * This method sets up the PayPal JavaScript SDK and renders the PayPal button to the specified
     * DOM element. Must be called after `prepare()` when PayPal is configured.
     *
     * The method performs the following:
     * - Validates that PayPal configuration is present
     * - Loads the PayPal JavaScript SDK using the provided clientId and currency
     * - Configures the PayPal button with funding source, createOrder, onApprove, onError, and onCancel handlers
     * - Calls `asyncCharge()` when payment is approved
     * - Emits the `PSP_PAYPAL_APPROVE_CALLBACK` event on successful approval
     * - Emits the `PSP_PAYPAL_ON_ERROR` event on errors
     * - Emits the `PSP_PAYPAL_ON_CANCEL` event if the user cancels
     * - Renders the PayPal button to the specified mounting element
     * - Emits the `PSP_PAYPAL_PREPARED` event when setup is complete
     *
     * Emits the following events:
     * - `PSP_PAYPAL_PREPARED` - when the PayPal SDK is loaded and button rendered
     * - `PSP_PAYPAL_APPROVE_CALLBACK` - when the user approves the payment
     * - `PSP_PAYPAL_ON_ERROR` - when an error occurs
     * - `PSP_PAYPAL_ON_CANCEL` - when the user cancels the payment
     *
     * @throws {Error} If PayPal configuration has not been set via `prepare()`.
     * @throws {Error} If PayPal SDK fails to load.
     */
    private async initPaypalSdk() {
        if (!this.paypalConfig) {
            throw new Error('PayPal config not available');
        }

        const clientId = this.requirePaymentConfigString(
            UpoSdk.PSP.paypal,
            'clientId',
        );
        const mountingElement = this.requirePaymentConfigString(
            UpoSdk.PSP.paypal,
            'mountingElement',
        );
        const createOrderCallback = this.requirePaymentConfigCallback(
            UpoSdk.PSP.paypal,
            'createOrderCallback',
        );
        const currency = this.paypalConfig.currency ?? 'USD';

        if (typeof window !== 'undefined') {
            loadScript({ clientId: clientId, currency: currency })
                .then((paypal) => {
                    if (!paypal) {
                        throw new Error('PayPal initialization failed');
                    }

                    let paypalButtonConfig: PayPalButtonsComponentOptions = {
                        fundingSource: paypal.FUNDING!.PAYPAL,
                        createOrder:
                            createOrderCallback as PayPalButtonCreateOrder,
                        onApprove: async (
                            data: OnApproveData,
                            actions: OnApproveActions,
                        ): Promise<void> => {
                            const chargeResult = this.asyncCharge(
                                UpoSdk.PSP.paypal,
                            );
                            this.emitEvent(
                                UpoSdk.EVENTS.PSP_PAYPAL_APPROVE_CALLBACK,
                                {},
                            );
                            return chargeResult;
                        },
                        onError: (err: Record<string, unknown>): void => {
                            this.emitEvent(UpoSdk.EVENTS.PSP_PAYPAL_ON_ERROR, {
                                err,
                            });
                        },
                        onCancel: (
                            data: Record<string, unknown>,
                            actions: OnCancelledActions,
                        ): void => {
                            this.emitEvent(UpoSdk.EVENTS.PSP_PAYPAL_ON_CANCEL, {
                                data,
                                actions,
                            });
                        },
                    };
                    if (
                        typeof this.paypalConfig?.onApproveCallback ===
                        'function'
                    ) {
                        paypalButtonConfig.onApprove =
                            this.paypalConfig?.onApproveCallback;
                    }

                    paypal.Buttons!(paypalButtonConfig).render(
                        mountingElement as string | HTMLElement,
                    );
                    this.emitEvent(UpoSdk.EVENTS.PSP_PAYPAL_PREPARED, {});
                })
                .catch((err) => {
                    throw new Error(
                        'Error loading PayPal SDK: ' + JSON.stringify(err),
                    );
                });
        }
    }

    private prepareToken(authToken: string): string {
        const trimmedToken = authToken.trim();
        return trimmedToken.startsWith('Bearer ')
            ? trimmedToken
            : `Bearer ${trimmedToken}`;
    }

    private emitEvent(name: string, data: object): void {
        const event = new CustomEvent(`${name}`, { detail: data });
        if (typeof window !== 'undefined') {
            window.dispatchEvent(event);
        }
    }

    /**
     * Initialize the Apple Pay SDK for payment processing.
     *
     * This method sets up the Apple Pay JavaScript SDK and renders an Apple Pay button to the
     * specified DOM element. Must be called after `prepare()` when Apple Pay is configured.
     *
     * The method performs the following:
     * - Checks for Apple Pay Session support (gracefully returns on non-Apple browsers)
     * - Validates Apple Pay configuration (mountingElement and applePayAmount)
     * - Loads the Apple Pay SDK script
     * - Creates an Apple Pay button element in the specified mounting element
     * - Configures the Apple Pay session with payment request details
     * - Sets up merchant validation via the `onValidateMerchantCallback`
     * - Handles payment authorization by calling `asyncCharge()` or the custom `onPaymentAuthorizedCallback`
     * - Emits events for completed, failed, and cancelled payments
     *
     * Emits the following events:
     * - `PSP_APPLEPAY_COMPLETED` - when payment is successfully charged
     * - `PSP_APPLEPAY_FAILED` - when payment fails
     * - `PSP_APPLEPAY_CANCELLED` - when the user cancels the payment
     *
     * @param {Object} applePayConfig - The Apple Pay configuration object.
     * @param {string} applePayConfig.mountingElement - The ID of the HTML element to render the button in.
     * @param {string} applePayConfig.applePayAmount - The amount to display on the Apple Pay sheet.
     * @param {string} applePayConfig.applePayCurrency - The currency code (e.g., 'USD').
     * @param {string} applePayConfig.countryCode - The country code (e.g., 'US').
     * @param {string[]} applePayConfig.supportedNetworks - Supported card networks (e.g., ['visa', 'mastercard']).
     * @param {string[]} applePayConfig.merchantCapabilities - Merchant capabilities (e.g., ['supports3DS']).
     * @param {string} applePayConfig.label - The merchant name/label to display.
     * @param {string} applePayConfig.buttonType - The button type to display.
     * @param {string} applePayConfig.locale - The locale for the button.
     * @param {function} applePayConfig.onValidateMerchantCallback - Callback to validate the merchant session.
     * @param {function} [applePayConfig.onPaymentAuthorizedCallback] - Optional custom callback for payment authorization.
     *
     * @throws {Error} If Apple Pay mounting element is not a string.
     * @throws {Error} If Apple Pay amount is not a string.
     * @throws {Error} If the mounting element cannot be found in the DOM.
     * @throws {Error} If the Apple Pay button cannot be created.
     */
    private initApplePaySdk(applePayConfig: ApplePayConfig): void {
        if (
            typeof window === 'undefined' ||
            !window.hasOwnProperty('ApplePaySession')
        ) {
            console.error(
                'Attempting to initialize ApplePay on a non-Apple browser or device',
            );
            return;
        }

        if (typeof applePayConfig.mountingElement !== 'string') {
            throw new Error(
                'ApplePay mounting element must be a string. Please check your configuration.',
            );
        }

        if (typeof applePayConfig.applePayAmount !== 'string') {
            throw new Error(
                'ApplePay amount must be a string. Please check your configuration.',
            );
        }

        const applePayScript = this.includeApplePayScript();
        applePayScript.addEventListener('load', () => {
            const mountingElement = document.getElementById(
                applePayConfig.mountingElement!.replace(/^#+/, ''),
            );
            if (!mountingElement) {
                throw new Error(
                    `Element with ID '${applePayConfig.mountingElement}' not found.`,
                );
            }

            mountingElement.innerHTML =
                '<apple-pay-button buttonstyle="black" id="upoSdkApplePayButton" type="' +
                applePayConfig.buttonType +
                '" locale="' +
                applePayConfig.locale +
                '"></apple-pay-button>';

            const applePayButton = document.getElementById(
                'upoSdkApplePayButton',
            );

            if (!applePayButton) {
                throw new Error(`ApplePay Button not found.`);
            }

            applePayButton.addEventListener('click', () => {
                const request: ApplePayJS.ApplePayPaymentRequest = {
                    countryCode: applePayConfig.countryCode,
                    currencyCode: applePayConfig.applePayCurrency,
                    supportedNetworks: applePayConfig.supportedNetworks,
                    merchantCapabilities:
                        applePayConfig.merchantCapabilities as ApplePayMerchantCapability[],
                    total: {
                        label: applePayConfig.label,
                        amount: applePayConfig.applePayAmount || '0',
                    },
                };
                const session = new ApplePaySession(3, request);

                session.onvalidatemerchant = async (
                    event: ApplePayValidateMerchantEvent,
                ) => {
                    try {
                        const callback = this.requirePaymentConfigCallback(
                            UpoSdk.PSP.adyenApplePay,
                            'onValidateMerchantCallback',
                        );
                        const sessionData = await callback(event);
                        session.completeMerchantValidation(sessionData);
                        return Promise.resolve();
                    } catch (error) {
                        console.error(error);
                        this.emitEvent(
                            UpoSdk.EVENTS
                                .PSP_APPLEPAY_SESSION_INITIALIZATION_FAILED,
                            error as object,
                        );
                    }
                };

                session.onpaymentauthorized = async (
                    event: ApplePayPaymentAuthorizedEvent,
                ) => {
                    try {
                        if (
                            typeof this.adyenApplePayConfig
                                ?.onPaymentAuthorizedCallback === 'function'
                        ) {
                            let result = {
                                status: ApplePaySession.STATUS_SUCCESS,
                            };
                            if (
                                !this.requirePaymentConfigCallback(
                                    UpoSdk.PSP.adyenApplePay,
                                    'onPaymentAuthorizedCallback',
                                )(event)
                            ) {
                                result.status = ApplePaySession.STATUS_FAILURE;
                            }
                            session.completePayment(result);
                        } else {
                            let result = {
                                status: ApplePaySession.STATUS_SUCCESS,
                            };
                            try {
                                await this.asyncCharge(
                                    UpoSdk.PSP.adyenApplePay,
                                    event,
                                );
                                this.emitEvent(
                                    UpoSdk.EVENTS.PSP_APPLEPAY_COMPLETED,
                                    {},
                                );
                            } catch (error) {
                                this.emitEvent(
                                    UpoSdk.EVENTS.PSP_APPLEPAY_FAILED,
                                    error as object,
                                );
                                result.status = ApplePaySession.STATUS_FAILURE;
                            } finally {
                                session.completePayment(result);
                            }
                        }
                    } catch (e) {
                        console.error(e);
                        throw e;
                    }
                };

                session.oncancel = (event) => {
                    console.log('ApplePay Cancelled');
                    this.emitEvent(UpoSdk.EVENTS.PSP_APPLEPAY_CANCELLED, {
                        data: event,
                    });
                };

                session.onpaymentmethodselected = (event) => {
                    console.log(event);
                    console.log('Network:', event.paymentMethod.network);
                    console.log('Type: ', event.paymentMethod.type);
                    const paymentTotal: ApplePayLineItem = {
                        label: applePayConfig.label,
                        amount: applePayConfig.applePayAmount || '0',
                    };
                    session.completePaymentMethodSelection({
                        newTotal: paymentTotal,
                    });
                    this.emitEvent(
                        UpoSdk.EVENTS.PSP_APPLEPAY_PAYMENT_METHOD_SELECTED,
                        {
                            data: event,
                        },
                    );
                };

                session.begin();
            });
        });
    }

    /**
     * Initialize the Samsung Pay SDK for payment processing.
     *
     * This method sets up the Samsung Pay Web SDK and renders a Samsung Pay button to the
     * specified DOM element. Must be called after `prepare()` when Samsung Pay is configured.
     *
     * The method performs the following:
     * - Validates Samsung Pay configuration (mountingElement and samsungPayAmount)
     * - Loads the Samsung Pay Web SDK script
     * - Creates a Samsung Pay PaymentClient with the specified environment (STAGE or PRODUCTION)
     * - Checks if the device is ready to pay with the configured payment methods
     * - Creates and adds the Samsung Pay button to the mounting element if ready to pay
     *
     * Emits the following events (via `createAndAddButton` and `onSamsungPayButtonClicked`):
     * - `PSP_SAMSUNGPAY_INITIALIZATION_FAILED` - when transaction initialization fails
     * - `PSP_SAMSUNGPAY_INITIALIZATION_COMPLETED` - when transaction initialization succeeds
     * - `PSP_SAMSUNGPAY_COMPLETED` - when payment is successfully charged
     * - `PSP_SAMSUNGPAY_CANCELLED` - when payment is cancelled
     * - `PSP_SAMSUNGPAY_LOAD_PAYMENT_SHEET_FAILED` - when loading the payment sheet fails
     *
     * @param {Object} samsungPayConfig - The Samsung Pay configuration object.
     * @param {string} samsungPayConfig.mountingElement - The ID of the HTML element to render the button in.
     * @param {string} samsungPayConfig.samsungPayAmount - The amount to display on the Samsung Pay sheet.
     * @param {string} samsungPayConfig.samsungPayCurrency - The currency code (e.g., 'USD').
     * @param {string} samsungPayConfig.invoiceId - The invoice ID for the transaction.
     * @param {string} samsungPayConfig.paymentReference - The payment reference for the transaction.
     * @param {string} samsungPayConfig.samsungPayServiceId - The Samsung Pay service ID.
     * @param {string} samsungPayConfig.protocol - The protocol to use for Samsung Pay.
     * @param {string} samsungPayConfig.environment - The environment ('test' or 'live').
     * @param {function} samsungPayConfig.onInitializeTransaction - Callback to initialize the transaction.
     *
     * @throws {Error} If Samsung Pay mounting element is not a string.
     * @throws {Error} If Samsung Pay amount is not a string.
     * @throws {Error} If the mounting element cannot be found in the DOM.
     */
    private initSamsungPaySdk(samsungPayConfig: SamsungPayConfig): void {
        console.log('samsungPayConfig');
        console.log(samsungPayConfig);
        if (typeof samsungPayConfig.mountingElement !== 'string') {
            throw new Error(
                'SamsungPay mounting element must be a string. Please check your configuration.',
            );
        }

        if (typeof samsungPayConfig.samsungPayAmount !== 'string') {
            throw new Error(
                'SamsungPay amount must be a string. Please check your configuration.',
            );
        }

        const samsungPayScript = this.includeSamsungPayScript();
        samsungPayScript.addEventListener('load', () => {
            const mountingElement = document.getElementById(
                samsungPayConfig.mountingElement!.replace(/^#+/, ''),
            );
            if (!mountingElement) {
                throw new Error(
                    `Element with ID '${samsungPayConfig.mountingElement}' not found.`,
                );
            }
            if (typeof window !== 'undefined' && window.SamsungPay) {
                const samsungPayClient: SamsungPay.PaymentClient =
                    new window.SamsungPay.PaymentClient({
                        environment:
                            samsungPayConfig.environment === 'test'
                                ? 'STAGE'
                                : 'PRODUCTION',
                    });

                let paymentMethods: SamsungPay.PaymentMethods = {
                    version: '2',
                    serviceId: samsungPayConfig.samsungPayServiceId || '',
                    protocol: samsungPayConfig.protocol,
                    allowedBrands: ['visa', 'mastercard'],
                };

                samsungPayClient
                    .isReadyToPay(paymentMethods)
                    .then((response: any) => {
                        console.log(
                            'samsungpay isReadyToPay:',
                            response.result,
                        );
                        if (response.result) {
                            this.createAndAddButton(
                                samsungPayClient,
                                paymentMethods,
                                mountingElement,
                            );
                        }
                        this.emitEvent(
                            UpoSdk.EVENTS.PSP_SAMSUNGPAY_IS_READY_TO_PAY,
                            {
                                isReadyToPay: response.result,
                            },
                        );
                    })
                    .catch((err: any) => {
                        console.error(err);
                        this.emitEvent(
                            UpoSdk.EVENTS.PSP_SAMSUNGPAY_IS_READY_TO_PAY_FAILED,
                            { err },
                        );
                    });
            }
        });
    }

    private includeApplePayScript(): HTMLScriptElement {
        const applePayScript = document.createElement('script');
        applePayScript.id = 'applepay-js';
        applePayScript.src =
            'https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js';
        document.head.append(applePayScript);
        return applePayScript;
    }

    private includeSamsungPayScript(): HTMLScriptElement {
        const samsungPayScript = document.createElement('script');
        samsungPayScript.id = 'samsungPay-js';
        samsungPayScript.src =
            'https://resource.stg.samsungpay.com/gsmpi/sdk/samsungpay_web_sdk.js';
        document.head.append(samsungPayScript);
        return samsungPayScript;
    }

    private createAndAddButton(
        samsungPayClient: SamsungPay.PaymentClient,
        paymentMethods: SamsungPay.PaymentMethods,
        mountingElement: HTMLElement,
    ) {
        const samsungPayButton = samsungPayClient.createButton({
            onClick: () => {
                void this.onSamsungPayButtonClicked(
                    samsungPayClient,
                    paymentMethods,
                );
            },
            buttonStyle: this.requirePaymentConfigString(
                UpoSdk.PSP.adyenSamsungPay,
                'buttonColor',
            ),
        });

        mountingElement.appendChild(samsungPayButton);
    }

    private async onSamsungPayButtonClicked(
        samsungPayClient: any,
        paymentMethods: any,
    ) {
        const onInitializeTransaction = this.requirePaymentConfigCallback(
            UpoSdk.PSP.adyenSamsungPay,
            'onInitializeTransaction',
        );
        const initResponse = await onInitializeTransaction();
        console.log('initResponse');
        console.log(initResponse);
        if (initResponse.statusCode !== 200) {
            console.log(initResponse);
            this.emitEvent(
                UpoSdk.EVENTS.PSP_SAMSUNGPAY_INITIALIZATION_FAILED,
                {},
            );
        }
        this.emitEvent(
            UpoSdk.EVENTS.PSP_SAMSUNGPAY_INITIALIZATION_COMPLETED,
            {},
        );
        const transactionDetail: SamsungPay.TransactionDetail = {
            orderNumber: this.requirePaymentConfigString(
                UpoSdk.PSP.adyenSamsungPay,
                'invoiceId',
            ),
            merchant: {
                name: this.requirePaymentConfigString(
                    UpoSdk.PSP.adyenSamsungPay,
                    'paymentReference',
                ),
                url: 'virtualshop.com',
                countryCode: 'US',
            },
            amount: {
                option: 'FORMAT_TOTAL_ESTIMATED_AMOUNT',
                currency: this.requirePaymentConfigString(
                    UpoSdk.PSP.adyenSamsungPay,
                    'samsungPayCurrency',
                ),
                total: this.requirePaymentConfigString(
                    UpoSdk.PSP.adyenSamsungPay,
                    'samsungPayAmount',
                ),
            },
        };
        samsungPayClient
            .loadPaymentSheet(paymentMethods, transactionDetail)
            .then(async (paymentCredential: any) => {
                this.emitEvent(
                    UpoSdk.EVENTS.PSP_SAMSUNGPAY_LOAD_PAYMENT_SHEET_LOADED,
                    {},
                );
                try {
                    await this.asyncCharge(
                        UpoSdk.PSP.adyenSamsungPay,
                        paymentCredential,
                    );
                    this.emitEvent(UpoSdk.EVENTS.PSP_SAMSUNGPAY_COMPLETED, {});
                } catch (error) {
                    this.emitEvent(
                        UpoSdk.EVENTS.PSP_SAMSUNGPAY_CANCELLED,
                        error as object,
                    );
                } finally {
                }
            })
            .catch((error: any) => {
                console.log('error');
                console.log(error);
                this.emitEvent(
                    UpoSdk.EVENTS.PSP_SAMSUNGPAY_LOAD_PAYMENT_SHEET_FAILED,
                    error as object,
                );
            });
    }

    /**
     * Initialize the Google Pay SDK.
     *
     * @param {Object} googlePayConfig - The Google Pay configuration.
     * @param {string} googlePayConfig.merchantProfileId - The Merchant Profile ID of the Merchant Profile that will be used to process the GooglePay payment
     * @param {string} googlePayConfig.storeId - The Store ID of the Store that will be used to process the GooglePay payment
     * @param {string} googlePayConfig.environment - The environment to run the SDK in 'test' or 'live' (default `test`) - see https://docs.adyen.com/development-resources/live-endpoints/#checkout-js-endpoints
     * @param {string} googlePayConfig.mountingElement - The ID of the html element that the GooglePay button will be created in
     * @param {string} googlePayConfig.googlePayAmount - The amount of the purchase to display on the GooglePay payment sheet
     * @param {string} googlePayConfig.googlePayCurrency - The currency to display on the GooglePay payment sheet, default `USD`
     * @param {string} googlePayConfig.merchantAccountName - The name of the merchant account
     * @param {string} [googlePayConfig.buttonColor] - The color of the google pay button 'white', 'black', or 'default' (default `default`)
     * @param {string} [googlePayConfig.buttonType] - The different types of google pay buttons (default `buy`)
     * @param {number} [googlePayConfig.buttonRadius] - The button corner radius of the button (default `0`)
     * @param {string} [googlePayConfig.buttonBorderType] - The border around the button `default_border` or ‘no_border’ (default `default_border`)
     * @param {string} [googlePayConfig.buttonSizeMode] - The button mode for the size (default `fill`)
     * @param {string} [googlePayConfig.buttonLocale] - The desired button language (default `en`)
     * @param {string} googlePayConfig.invoiceId - The invoice ID for the transaction
     * @param {string} googlePayConfig.paymentReference - The payment reference for the transaction
     * @param {string} [googlePayConfig.locale] - The locale to pass to the GooglePay SDK (default `en-US` )
     * @param {string} [googlePayConfig.countryCode] - The country code to pass to GooglePay SDK (default `US`)
     * @param {function} googlePayConfig.onInitializeTransaction - A function that will initialize the transaction (see below)
     * @return {Promise<void>} Returns a promise that resolves when initialization is complete.
     */
    private async initGooglePaySdk(
        googlePayConfig: GooglePayConfig,
    ): Promise<void> {
        if (typeof window === 'undefined') {
            console.error(
                'Attempting to initialize Google Pay in a non-browser environment',
            );
            return;
        }

        try {
            // Validate configuration before proceeding
            this.validateGooglePayConfig(googlePayConfig);

            // Load Google Pay script if not already loaded
            await this.loadGooglePayScript();

            // Create payment request
            const paymentRequest =
                await this.createPaymentRequest(googlePayConfig);

            // Setup Google Pay button
            await this.setupGooglePayButton(googlePayConfig, paymentRequest);
        } catch (error) {
            console.error('Failed to initialize Google Pay SDK:', error);
            this.emitEvent(UpoSdk.EVENTS.PSP_GOOGLEPAY_FAILED, error as object);
            throw error;
        }
    }

    /**
     * Validate the Google Pay configuration.
     *
     * @param {Object} config - The Google Pay configuration.
     * @param {string} config.merchantProfileId - The Merchant Profile ID of the Merchant Profile that will be used to process the GooglePay payment
     * @param {string} config.storeId - The Store ID of the Store that will be used to process the GooglePay payment
     * @param {string} config.environment - The environment to run the SDK in 'test' or 'live' (default `test`) - see https://docs.adyen.com/development-resources/live-endpoints/#checkout-js-endpoints
     * @param {string} config.mountingElement - The ID of the html element that the GooglePay button will be created in
     * @param {string} config.googlePayAmount - The amount of the purchase to display on the GooglePay payment sheet
     * @param {string} config.googlePayCurrency - The currency to display on the GooglePay payment sheet, default `USD`
     * @param {string} config.merchantAccountName - The name of the merchant account
     * @param {string} [config.buttonColor] - The color of the google pay button 'white', 'black', or 'default' (default `default`)
     * @param {string} [config.buttonType] - The different types of google pay buttons (default `buy`)
     * @param {number} [config.buttonRadius] - The button corner radius of the button (default `0`)
     * @param {string} [config.buttonBorderType] - The border around the button `default_border` or ‘no_border’ (default `default_border`)
     * @param {string} [config.buttonSizeMode] - The button mode for the size (default `fill`)
     * @param {string} [config.buttonLocale] - The desired button language (default `en`)
     * @param {string} config.invoiceId - The invoice ID for the transaction
     * @param {string} config.paymentReference - The payment reference for the transaction
     * @param {string} [config.locale] - The locale to pass to the GooglePay SDK (default `en-US` )
     * @param {string} [config.countryCode] - The country code to pass to GooglePay SDK (default `US`)
     * @param {function} config.onInitializeTransaction - A function that will initialize the transaction (see below)
     * @throws {Error} If the configuration is invalid.
     */
    private validateGooglePayConfig(config: GooglePayConfig): void {
        if (!config.mountingElement) {
            throw new Error(
                'mountingElement is required for Google Pay configuration',
            );
        }

        if (!config.googlePayAmount) {
            throw new Error(
                'googlePayAmount is required for Google Pay configuration',
            );
        }

        // Validate buttonType is one of the allowed values
        const validButtonTypes = [
            'buy',
            'plain',
            'book',
            'donate',
            'order',
            'support',
            'signin',
        ];
        if (
            config.buttonType &&
            !validButtonTypes.includes(config.buttonType)
        ) {
            throw new Error(
                `Invalid buttonType: ${config.buttonType}. Must be one of: ${validButtonTypes.join(', ')}`,
            );
        }

        // Validate environment is one of the allowed values
        const validEnvironments = ['test', 'live'];
        if (
            config.environment &&
            !validEnvironments.includes(config.environment)
        ) {
            throw new Error(
                `Invalid environment: ${config.environment}. Must be one of: ${validEnvironments.join(', ')}`,
            );
        }
    }

    /**
     * Load the Google Pay script.
     *
     * @return {Promise<void>} Returns a promise that resolves when the script is loaded.
     */
    private async loadGooglePayScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            // Check if script is already loaded
            if (document.getElementById('google-pay-js')) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.id = 'google-pay-js';
            script.src = 'https://pay.google.com/gp/p/js/pay.js';
            script.async = true;
            script.defer = true;

            script.onload = () => {
                resolve();
            };

            script.onerror = (error) => {
                reject(new Error(`Failed to load Google Pay script: ${error}`));
            };

            document.head.appendChild(script);
        });
    }

    private async createPaymentRequest(
        config: GooglePayConfig,
    ): Promise<google.payments.api.PaymentDataRequest> {
        // Validate required fields
        if (!config.googlePayAmount) {
            throw new Error(
                'googlePayAmount is required for Google Pay configuration',
            );
        }

        // Create base payment request with minimal required fields
        const paymentRequest: google.payments.api.PaymentDataRequest = {
            apiVersion: 2,
            apiVersionMinor: 0,
            merchantInfo: {
                merchantId: this.requirePaymentConfigString(
                    UpoSdk.PSP.adyenGooglePay,
                    'merchantId',
                ),
                merchantName: config.merchantName,
            },
            allowedPaymentMethods: [
                {
                    type: 'CARD',
                    parameters: {
                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: [
                            'AMEX',
                            'DISCOVER',
                            'INTERAC',
                            'JCB',
                            'MASTERCARD',
                            'VISA',
                        ],
                    },
                    tokenizationSpecification: {
                        type: 'PAYMENT_GATEWAY',
                        parameters: {
                            gateway: this.requirePaymentConfigString(
                                UpoSdk.PSP.adyenGooglePay,
                                'gateway',
                            ),
                            gatewayMerchantId: this.requirePaymentConfigString(
                                UpoSdk.PSP.adyenGooglePay,
                                'merchantAccountName',
                            ),
                        },
                    },
                },
            ],
            transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPrice: config.googlePayAmount,
                currencyCode: config.googlePayCurrency,
            },
        };

        // Add offer info if configured
        if (config.offers && config.offers.length > 0) {
            paymentRequest.offerInfo = {
                offers: config.offers.map((offer) => ({
                    redemptionCode: offer.redemptionCode ?? 'redemptionCode',
                    description: offer.description ?? 'description',
                })),
            };
        }

        return paymentRequest;
    }

    private async setupGooglePayButton(
        config: GooglePayConfig,
        paymentRequest: google.payments.api.PaymentDataRequest,
    ): Promise<void> {
        // Wait for Google Pay API to be ready
        if (!window.google?.payments) {
            throw new Error('Google Pay API not available');
        }

        // Find the mounting element
        if (!config.mountingElement) {
            throw new Error(
                'mountingElement is required for Google Pay configuration',
            );
        }

        const mountingElement =
            document.getElementById(
                config.mountingElement.replace(/^#+/, ''),
            ) || document.querySelector(config.mountingElement);

        if (!mountingElement) {
            throw new Error(
                `Mounting element with ID '${config.mountingElement}' not found`,
            );
        }

        // Create the Google Pay button
        const googlePaymentsClient = new google.payments.api.PaymentsClient({
            environment: config.environment === 'live' ? 'PRODUCTION' : 'TEST',
        });

        const isReadyToPayRequest: google.payments.api.IsReadyToPayRequest = {
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [
                {
                    type: 'CARD',
                    parameters: {
                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: [
                            'AMEX',
                            'DISCOVER',
                            'INTERAC',
                            'JCB',
                            'MASTERCARD',
                            'VISA',
                        ],
                    },
                    tokenizationSpecification: {
                        type: 'PAYMENT_GATEWAY',
                        parameters: {
                            gateway: this.requirePaymentConfigString(
                                UpoSdk.PSP.adyenGooglePay,
                                'gateway',
                            ),
                            gatewayMerchantId: this.requirePaymentConfigString(
                                UpoSdk.PSP.adyenGooglePay,
                                'merchantAccountName',
                            ),
                        },
                    },
                },
            ],
        };

        googlePaymentsClient
            .isReadyToPay(isReadyToPayRequest)
            .then((response: google.payments.api.IsReadyToPayResponse) => {
                console.log('googlepay isReadyToPay:', response.result);
                if (response.result) {
                    const button = googlePaymentsClient.createButton({
                        buttonColor: config.buttonColor as ButtonColor,
                        buttonType: config.buttonType as ButtonType,
                        buttonRadius: config.buttonRadius,
                        buttonSizeMode: config.buttonSizeMode as ButtonSizeMode,
                        buttonBorderType:
                            config.buttonBorderType as ButtonBorderType,
                        buttonLocale: config.buttonLocale,
                        onClick: async () => {
                            try {
                                const onInitializeTransaction =
                                    this.requirePaymentConfigCallback(
                                        UpoSdk.PSP.adyenGooglePay,
                                        'onInitializeTransaction',
                                    );
                                const initResponse =
                                    await onInitializeTransaction();
                                console.log('initResponse' + initResponse);
                                if (initResponse.statusCode !== 200) {
                                    this.emitEvent(
                                        UpoSdk.EVENTS
                                            .PSP_GOOGLEPAY_INITIALIZATION_FAILED,
                                        {},
                                    );
                                }

                                const paymentData =
                                    await googlePaymentsClient.loadPaymentData(
                                        paymentRequest,
                                    );

                                if (
                                    paymentData.paymentMethodData
                                        .tokenizationData != null
                                ) {
                                    this.emitEvent(
                                        UpoSdk.EVENTS
                                            .PSP_GOOGLEPAY_LOAD_PAYMENT_SHEET_LOADED,
                                        {},
                                    );
                                    await this.asyncCharge(
                                        UpoSdk.PSP.adyenGooglePay,
                                        paymentData,
                                    );
                                    this.emitEvent(
                                        UpoSdk.EVENTS.PSP_GOOGLEPAY_COMPLETED,
                                        {},
                                    );
                                }
                            } catch (error) {
                                const paymentsError: google.payments.api.PaymentsError =
                                    error as google.payments.api.PaymentsError;
                                this.handleGooglePayPaymentErrorWithEvents(
                                    paymentsError,
                                );
                            }
                        },
                    });

                    // Clear the mounting element and add the button
                    mountingElement.innerHTML = '';
                    mountingElement.appendChild(button);
                } else {
                    console.log(
                        'Google Pay not available: no eligible payment methods found for this user/device.',
                    );
                }
                this.emitEvent(UpoSdk.EVENTS.PSP_GOOGLEPAY_IS_READY_TO_PAY, {
                    isReadyToPay: response.result,
                });
            })
            .catch((err: any) => {
                console.error(err);
                this.emitEvent(
                    UpoSdk.EVENTS.PSP_GOOGLEPAY_IS_READY_TO_PAY_FAILED,
                    { err },
                );
            });
    }

    private handleGooglePayPaymentErrorWithEvents(
        err: google.payments.api.PaymentsError,
    ) {
        console.error('Google Pay error:', err);

        switch (err.statusCode) {
            case 'CANCELED':
                console.error('User canceled Google Pay:', err.statusMessage);
                this.emitEvent(UpoSdk.EVENTS.PSP_GOOGLEPAY_CANCELLED, {
                    statusCode: err.statusCode,
                    statusMessage: err.statusMessage,
                });
                break;

            case 'BUYER_ACCOUNT_ERROR':
                console.error('Buyer account error', err.statusMessage);
                this.emitEvent(
                    UpoSdk.EVENTS.PSP_GOOGLEPAY_BUYER_ACCOUNT_ERROR,
                    {
                        statusCode: err.statusCode,
                        statusMessage: err.statusMessage,
                    },
                );
                break;

            case 'DEVELOPER_ERROR':
                console.error('Developer error:', err.statusMessage);
                this.emitEvent(UpoSdk.EVENTS.PSP_GOOGLEPAY_DEVELOPER_ERROR, {
                    statusCode: err.statusCode,
                    statusMessage: err.statusMessage,
                });
                break;

            case 'MERCHANT_ACCOUNT_ERROR':
                console.error('Merchant account issue:', err.statusMessage);
                // Emit second event with both statusCode and statusMessage
                this.emitEvent(
                    UpoSdk.EVENTS.PSP_GOOGLEPAY_MERCHANT_ACCOUNT_ERROR,
                    {
                        statusCode: err.statusCode,
                        statusMessage: err.statusMessage,
                    },
                );
                break;

            case 'INTERNAL_ERROR':
                console.error(
                    'Something went wrong. Please try again.',
                    err.statusMessage,
                );
                this.emitEvent(UpoSdk.EVENTS.PSP_GOOGLEPAY_FAILED, {
                    statusCode: err.statusCode,
                    statusMessage: err.statusMessage,
                });
                break;

            default:
                console.error(
                    'Unable to complete payment. Please try again.',
                    err.statusMessage,
                );
                this.emitEvent(UpoSdk.EVENTS.PSP_GOOGLEPAY_FAILED, {
                    statusCode: err.statusCode,
                    statusMessage: err.statusMessage,
                });
        }
    }
}

export default UpoSdk;

if (typeof window !== 'undefined') {
    window.UpoSdk = UpoSdk;
}
